/**
 * Session Recording Hook
 *
 * Handles continuous audio recording for consonant practice sessions.
 * Records to WebM/Opus, uploads to Supabase Storage, returns storage path.
 *
 * startRecording() accepts an optional external MediaStream from useMicStream.
 * When provided, the hook creates its MediaRecorder from the shared stream and
 * does NOT stop its tracks on cleanup (the stream owner handles lifecycle).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  isMediaDevicesSupported,
  isSecureContext,
  requestMicrophone,
  stopMediaStream,
  getMediaErrorDetails,
} from '@/lib/media';
import { supabase } from '@/lib/supabase/client';

const BUCKET = 'consonant-recordings';
const UPLOAD_MAX_RETRIES = 2;
const UPLOAD_RETRY_DELAY_MS = 1500;

export interface UseSessionRecordingParams {
  enabled: boolean;
  childId?: string;
}

export interface UseSessionRecordingResult {
  isRecording: boolean;
  startRecording: (externalStream?: MediaStream | null) => Promise<void>;
  stopRecording: () => Promise<string | null>;
  error: string | null;
  hasPermission: boolean;
}

export function useSessionRecording({
  enabled,
  childId,
}: UseSessionRecordingParams): UseSessionRecordingResult {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const isExternalRef = useRef(false);
  const uploadingRef = useRef(false);
  const unmountedRef = useRef(false);

  const startRecording = useCallback(async (externalStream?: MediaStream | null) => {
    if (!enabled) return;

    try {
      setError(null);
      audioChunksRef.current = [];

      let stream: MediaStream;

      if (externalStream) {
        stream = externalStream;
        isExternalRef.current = true;
      } else {
        if (!isMediaDevicesSupported()) {
          const details = getMediaErrorDetails(new Error('MediaDevices not supported'));
          setError(details.message);
          return;
        }
        if (!isSecureContext()) {
          const details = getMediaErrorDetails(new Error('Not secure context'));
          setError(details.message);
          return;
        }
        stream = await requestMicrophone({
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        });
        isExternalRef.current = false;
      }

      streamRef.current = stream;
      setHasPermission(true);

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(100);
      setIsRecording(true);
    } catch (err) {
      const details = getMediaErrorDetails(err);
      setError(details.message);
      setHasPermission(false);
      if (!isExternalRef.current) {
        stopMediaStream(streamRef.current);
      }
      streamRef.current = null;
    }
  }, [enabled]);

  const stopRecording = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        setIsRecording(false);
        if (!isExternalRef.current) {
          stopMediaStream(streamRef.current);
        }
        streamRef.current = null;
        resolve(null);
        return;
      }

      mediaRecorder.onstop = async () => {
        if (!isExternalRef.current) {
          stopMediaStream(streamRef.current);
        }
        streamRef.current = null;
        mediaRecorderRef.current = null;

        const blob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        audioChunksRef.current = [];
        setIsRecording(false);

        if (blob.size === 0) {
          resolve(null);
          return;
        }

        uploadingRef.current = true;
        try {
          const folder = childId ?? 'anon';
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const path = `${folder}/${timestamp}.webm`;

          let lastUploadError: unknown = null;
          for (let attempt = 0; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
            const { error: uploadError } = await supabase.storage
              .from(BUCKET)
              .upload(path, blob, {
                contentType: mediaRecorder.mimeType,
                upsert: false,
              });

            if (!uploadError) {
              resolve(path);
              return;
            }

            lastUploadError = uploadError;
            if (attempt < UPLOAD_MAX_RETRIES) {
              console.warn(`[SessionRecording] Upload attempt ${attempt + 1} failed, retrying...`);
              await new Promise(r => setTimeout(r, UPLOAD_RETRY_DELAY_MS * (attempt + 1)));
            }
          }

          console.error('[SessionRecording] Upload failed after retries:', lastUploadError);
          if (!unmountedRef.current) setError('Failed to save recording');
          resolve(null);
        } catch (err) {
          console.error('[SessionRecording] Upload error:', err);
          if (!unmountedRef.current) setError('Failed to save recording');
          resolve(null);
        } finally {
          uploadingRef.current = false;
        }
      };

      mediaRecorder.stop();
    });
  }, [childId]);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (!isExternalRef.current) {
        stopMediaStream(streamRef.current);
      }
      streamRef.current = null;
      mediaRecorderRef.current = null;
    };
  }, []);

  return {
    isRecording,
    startRecording,
    stopRecording,
    error,
    hasPermission,
  };
}
